import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useModules } from '../stores/modules.registry';

/**
 * Hook personalizado para obtener los módulos autorizados para el usuario
 * Now uses centralized ModulesRegistry store to avoid duplicate requests
 * @returns {Object} Estado de módulos autorizados y loading
 */
const useAuthorizedModules = () => {
  const { userInfo, role, activeOrganization, loading, currentUser } = useAuth();
  
  // 🔍 LOG: Estado del contexto de autenticación
  console.log("🔍 [useAuthorizedModules] === ESTADO DEL CONTEXTO ===");
  console.log("🔍 [useAuthorizedModules] role desde contexto:", role);
  console.log("🔍 [useAuthorizedModules] activeOrganization:", activeOrganization);
  console.log("🔍 [useAuthorizedModules] activeOrganization?.organization_id:", activeOrganization?.organization_id);
  console.log("🔍 [useAuthorizedModules] userInfo?.user?.role_code:", userInfo?.user?.role_code);
  console.log("🔍 [useAuthorizedModules] userInfo?.user?.role:", userInfo?.user?.role);
  console.log("🔍 [useAuthorizedModules] currentUser?.email:", currentUser?.email);
  
  // Get email from userInfo (backend) or currentUser (Firebase) as fallback
  const userEmail = userInfo?.user?.email || currentUser?.email;
  
  // Get role from multiple sources with fallback chain:
  // 1. role (from AuthContext state - most reliable after initial load)
  // 2. userInfo?.user?.role_code (from backend response - reliable during relogin)
  // 3. userInfo?.user?.members?.[0]?.role (from members array - fallback)
  // 4. activeOrganization?.role_code (from active org - last resort)
  const effectiveRole = role || 
                        userInfo?.user?.role_code || 
                        userInfo?.user?.members?.[0]?.role || 
                        activeOrganization?.role_code || 
                        undefined;
  
  console.log("🔍 [useAuthorizedModules] userEmail:", userEmail);
  console.log("🔍 [useAuthorizedModules] role:", role);
  console.log("🔍 [useAuthorizedModules] userInfo?.user?.role_code:", userInfo?.user?.role_code);
  console.log("🔍 [useAuthorizedModules] effectiveRole:", effectiveRole);
  console.log("🔍 [useAuthorizedModules] ¿Es super_user?:", effectiveRole === 'super_user');
  
  // Pass activeOrganization, role, and email to useModules so it uses React context instead of localStorage
  const { userModules, orgModules, loading: moduleLoading, error, refresh, isModuleAuthorized } = useModules({
    orgId: activeOrganization?.organization_id,
    role: effectiveRole,
    email: userEmail || undefined,
  });
  
  console.log("🔍 [useAuthorizedModules] === PASANDO A useModules ===");
  console.log("🔍 [useAuthorizedModules] orgId pasado:", activeOrganization?.organization_id);
  console.log("🔍 [useAuthorizedModules] role pasado:", effectiveRole);
  console.log("🔍 [useAuthorizedModules] email pasado:", userEmail || undefined);
  
  // Si no hay email, asegurar que moduleLoading es false para no bloquear ProtectedRoute
  // Para super_user, no bloquear por modulesLoading ya que tienen acceso total
  // Esto previene que ProtectedRoute se quede bloqueado esperando módulos
  const effectiveModuleLoading = !userEmail 
    ? false  // Sin email = no bloquear
    : (effectiveRole === 'super_user' 
        ? false  // super_user = no bloquear (acceso total)
        : moduleLoading);  // Otros usuarios = usar el loading real
  
  console.log(`[useAuthorizedModules] moduleLoading=${moduleLoading}, effectiveModuleLoading=${effectiveModuleLoading}, userEmail=${!!userEmail}, effectiveRole=${effectiveRole}`);

  // Track last call to prevent rapid consecutive calls
  const lastCallRef = useRef(null);
  const DEBOUNCE_MS = 500; // Minimum time between calls (500ms)

  // Load modules when auth context changes (user, org, role)
  useEffect(() => {
    console.log(`[useAuthorizedModules] useEffect triggered - loading=${loading}, hasEmail=${!!userEmail}, email=${userEmail}, orgId=${activeOrganization?.organization_id}`);
    console.log(`[useAuthorizedModules] userInfo?.user?.email=${userInfo?.user?.email}, currentUser?.email=${currentUser?.email}`);
    
    // IMPORTANTE: No ejecutar si Firebase aún está cargando
    if (loading) {
      console.log(`[useAuthorizedModules] Skipping - still loading`);
      return;
    }

    // Si no hay email, no intentar cargar módulos
    // El effectiveModuleLoading ya está configurado para ser false en este caso
    if (!userEmail) {
      console.log(`[useAuthorizedModules] No email available yet (userInfo?.user?.email=${userInfo?.user?.email}, currentUser?.email=${currentUser?.email})`);
      console.log(`[useAuthorizedModules] effectiveModuleLoading will be false, allowing ProtectedRoute to proceed`);
      return;
    }

    const orgId = activeOrganization?.organization_id;
    const role = effectiveRole;

    // Check if this is a duplicate call (same params within debounce window)
    const now = Date.now();
    const lastCall = lastCallRef.current;
    if (lastCall && 
        lastCall.email === userEmail && 
        lastCall.orgId === orgId && 
        lastCall.role === role &&
        (now - lastCall.timestamp) < DEBOUNCE_MS) {
      console.log(`[useAuthorizedModules] Skipping duplicate call (debounced) - same params called ${now - lastCall.timestamp}ms ago`);
      return;
    }

    // Update last call tracking
    lastCallRef.current = { email: userEmail, orgId, role, timestamp: now };

    // Para super_user, no necesitamos cargar módulos (tienen acceso total)
    // Pero cargamos de todos modos para tener la lista completa disponible
    if (effectiveRole === 'super_user') {
      console.log(`[useAuthorizedModules] super_user detected - loading modules anyway but won't block on loading state`);
    }

    // Force refresh when organization changes to ensure we get the right modules
    console.log(`[useAuthorizedModules] Loading modules for org ${orgId} (${activeOrganization?.organization_name || 'unknown'})`);
    
    // Load modules from centralized store (will dedupe and cache automatically)
    // Pass orgId and role from React context to ensure we use the correct organization
    console.log(`[useAuthorizedModules] Calling refresh(false)...`);
    let isCancelled = false;
    
    refresh(false).then(() => {
      if (!isCancelled) {
        console.log(`[useAuthorizedModules] refresh() completed`);
      }
    }).catch((err) => {
      if (!isCancelled) {
        console.error(`[useAuthorizedModules] refresh() failed:`, err);
        // El registry ya establece loading=false en caso de error
      }
    });
    
    // Cleanup: marcar como cancelado si el efecto se desmonta o las dependencias cambian
    return () => {
      isCancelled = true;
    };
  }, [userInfo?.user?.email, currentUser?.email, activeOrganization?.organization_id, effectiveRole, loading, refresh]);

  // Wrapper function that uses the centralized store's isModuleAuthorized
  // but adds logging for debugging (can be removed later if not needed)
  // IMPORTANT: Use effectiveRole to ensure super_user check works correctly even during relogin
  const checkModuleAuthorized = (moduleCode) => {
    // For super users, always allow access regardless of org modules
    if (effectiveRole === 'super_user') {
      console.log("=== VERIFICACIÓN DE MÓDULO ===");
      console.log(`Verificando autorización para módulo: "${moduleCode}"`);
      console.log(`Rol del usuario: ${effectiveRole} (SUPER USER - acceso total)`);
      console.log(`✅ Acceso permitido (super_user)`);
      return true;
    }
    
    const result = isModuleAuthorized(moduleCode);
    console.log("=== VERIFICACIÓN DE MÓDULO ===");
    console.log(`Verificando autorización para módulo: "${moduleCode}"`);
    console.log(`Rol del usuario: ${effectiveRole || 'null'}`);
    console.log(`Organización activa: ${activeOrganization?.organization_name || 'No seleccionada'}`);
    console.log(`Módulos autorizados: ${orgModules.map(m => m.code).join(', ')}`);
    console.log(`${result ? '✅' : '❌'} Acceso ${result ? 'permitido' : 'denegado'}`);
    return result;
  };

  return {
    authorizedModules: userModules,
    organizationModules: orgModules,
    loading: effectiveModuleLoading, // Usar effectiveModuleLoading en lugar de moduleLoading
    error,
    isModuleAuthorized: checkModuleAuthorized
  };
};

export default useAuthorizedModules;